import * as Analytics from "page/analytics";
import moment from "moment/moment";
import Constants from "const/constants";
import type { Encoder, ElasticEncoder } from "api/ElasticEncoder.types";
import { EncodeState } from "api/ElasticEncoder.types";

export default class ProducerElasticEncoder {
    encodeId: string;
    streamId: string;
    modelInitializationTime = moment.utc().unix() * 1000;
    intervalEncodeId?: ReturnType<typeof setInterval>;
    updateInterval?: ReturnType<typeof setInterval>;
    encodeState: EncodeState;
    elasticEncoderState: EncodeState;
    elasticEncoderObject: ElasticEncoder;
    onStateChange?: (enc: ProducerElasticEncoder) => void;
    encoderAllocationTracked: boolean;

    constants = {
        ESTIMATED_ELASTIC_ENCODE_WAIT: 150 * 1000
    };

    constructor({
        streamId,
        encodeId,
        autoUpdate = true
    }: {
        streamId: string;
        encodeId: string;
        autoUpdate?: boolean;
    }) {
        this.encodeId = encodeId;
        this.streamId = streamId;

        if (autoUpdate) {
            this.setTimestampUpdateInterval();
        }
    }

    url = (): string => {
        return `${Constants.DEFAULT_API_PATH}/streams/${this.streamId}/encode/producer`;
    };

    encodeUrl = (): string => {
        return `${this.url()}/access`;
    };

    pollEncodeState = (localInterval: number) => {
        this.stopPollEncodeState();
        this.fetchEncodeState();
        this.intervalEncodeId = setInterval(
            this.fetchEncodeState,
            localInterval
        );
    };

    stopPollEncodeState = () => {
        if (this.intervalEncodeId) {
            clearInterval(this.intervalEncodeId);
            this.intervalEncodeId = undefined;
        }
    };

    fetchEncodeState = async () => {
        return fetch(this.url(), { method: `GET` })
            .then((response) => response.json())
            .then(this.refreshElasticEncoderState);
    };

    refreshElasticEncoderState = ({ data }: { data: Encoder }) => {
        this.encodeState = data.state;
        const previousState = this.elasticEncoderState;

        if (this.isEncodeReady()) {
            this.elasticEncoderObject = data.elasticEncoder;
            if (this.isElasticEncoderObjectReady()) {
                this.elasticEncoderState = data.elasticEncoder.state;
            } else {
                this.elasticEncoderState = EncodeState.Pending;
            }

            if (this.isReady()) {
                this.stopPollEncodeState();
            }
        }

        if (previousState !== this.elasticEncoderState) {
            if (this.onStateChange) {
                this.onStateChange(this);
            }
        }
    };

    setTimestampUpdateInterval = () => {
        this.updateLastAccessedTimeStamp();
        this.updateInterval = setInterval(
            this.updateLastAccessedTimeStamp,
            5 * 60 * 1000
        );
    };

    updateLastAccessedTimeStamp = async () => {
        return fetch(this.encodeUrl(), { method: `POST` });
    };

    isEncodeReady = () => {
        return (
            !!this.encodeState &&
            typeof this.encodeState === `string` &&
            this.encodeState.toLowerCase() === EncodeState.Running
        );
    };

    isElasticEncoderObjectReady = (): boolean => {
        return !!this.elasticEncoderObject;
    };

    isReady = (): boolean => {
        return (
            !!this.elasticEncoderState &&
            typeof this.elasticEncoderState === `string` &&
            this.elasticEncoderState.toLowerCase() === EncodeState.Running
        );
    };

    // Provide a dumb guess at how long the user will have to wait before cloud producer is ready to be used
    getEstimatedTimeRemaining = (): number => {
        const projectedCompletionTimestamp =
            this.getProjectedCompletionTimeStamp();
        const currentTime = moment.utc().unix() * 1000;
        const estimatedTimeRemaining =
            projectedCompletionTimestamp - currentTime;

        return estimatedTimeRemaining >= 0 ? estimatedTimeRemaining : 0;
    };

    getProjectedCompletionTimeStamp = (): number => {
        const maxTime = this.constants.ESTIMATED_ELASTIC_ENCODE_WAIT;
        if (!this.isElasticEncoderObjectReady()) {
            return this.modelInitializationTime + maxTime;
        }

        const elasticEncoderObject = this.elasticEncoderObject;

        // use the time when the process started so we can sync between users / keep consistent on refresh
        const creationTimeStamp = elasticEncoderObject.creationTime;
        return creationTimeStamp + maxTime;
    };

    getPercentageTimeRemaining = (): number => {
        const timeRemaining = this.getEstimatedTimeRemaining();
        const maxTime = this.constants.ESTIMATED_ELASTIC_ENCODE_WAIT;
        const percentage = (timeRemaining / maxTime) * 100;

        return Math.round(percentage);
    };
}
